<template>
  <div class="social-proof">
    <!-- Stats Variant -->
    <div v-if="variant === 'stats'" class="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
      <div
        v-for="item in filteredItems"
        :key="item.id"
        class="animate-fade-in"
      >
        <div class="text-4xl sm:text-5xl font-bold text-brand-primary mb-2">
          {{ formatStatValue(item.value) }}
        </div>
        <div class="text-gray-300 text-sm sm:text-base font-medium">
          {{ item.label }}
        </div>
      </div>
    </div>

    <!-- Testimonials Variant -->
    <div v-else-if="variant === 'testimonials'" class="space-y-6">
      <div
        v-for="item in filteredItems"
        :key="item.id"
        class="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all"
      >
        <div class="flex items-start space-x-4">
          <img
            v-if="item.image"
            :src="item.image"
            :alt="item.author"
            class="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
          <div v-else class="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
            <span class="text-white font-bold text-lg">
              {{ getInitials(item.author) }}
            </span>
          </div>

          <div class="flex-1">
            <div class="flex items-center mb-2">
              <div class="flex text-yellow-400">
                <svg v-for="i in 5" :key="i" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              </div>
            </div>

            <p class="text-gray-300 mb-3 italic">
              "{{ item.value }}"
            </p>

            <div>
              <p class="text-white font-semibold">{{ item.author }}</p>
              <p class="text-gray-400 text-sm">
                {{ item.role }}
                <span v-if="item.company"> • {{ item.company }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Logos Variant -->
    <div v-else-if="variant === 'logos'" class="space-y-6">
      <p v-if="showLabel" class="text-center text-gray-400 text-sm font-medium uppercase tracking-wide">
        Trusted by Canadian Businesses
      </p>
      <div class="flex flex-wrap items-center justify-center gap-8">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all"
        >
          <img
            v-if="item.image"
            :src="item.image"
            :alt="item.company"
            class="h-8 sm:h-10 w-auto object-contain"
          />
          <div v-else class="text-gray-400 text-sm font-medium">
            {{ item.company }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
    validator: (items) => {
      return items.every(item => item.type && (item.value || item.label || item.company));
    }
  },
  variant: {
    type: String,
    required: true,
    validator: (value) => ['stats', 'testimonials', 'logos'].includes(value)
  },
  showLabel: {
    type: Boolean,
    default: true
  }
});

// Filter items by variant type
const filteredItems = computed(() => {
  return props.items.filter(item => item.type === props.variant);
});

// Format stat values (e.g., 5000 -> "5,000+" or 4.9 -> "4.9")
const formatStatValue = (value) => {
  if (typeof value === 'number') {
    if (value >= 1000) {
      return value.toLocaleString() + '+';
    }
    return value.toString();
  }
  return value;
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};
</script>
